/**
 * src/components/ledger/TransactionRow.tsx
 * High-density glassmorphic ledger row item with category pill and admin actions
 */

import React, { useState } from 'react';
import { 
  ArrowUpRight, ArrowDownLeft, Edit2, Trash2, 
  User, Calendar, Utensils, Coffee, Smartphone, Banknote, Tag, Home, Car,
  Paperclip, Image as ImageIcon
} from 'lucide-react';
import type { TransactionItem } from '../../services/api';
import { GlassBadge } from '../ui/GlassBadge';
import { LightboxModal } from '../ui/LightboxModal';

interface TransactionRowProps {
  transaction: TransactionItem;
  isAdmin: boolean;
  onEdit?: (tx: TransactionItem) => void;
  onDelete?: (id: number) => void;
}

export const TransactionRow: React.FC<TransactionRowProps> = ({
  transaction,
  isAdmin,
  onEdit,
  onDelete
}) => {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const isReceive = transaction.transaction_type === 'receive';

  // Resolve Apple squircle class & icon
  const getCategoryTheme = (cat?: string | null) => {
    const c = (cat || '').toLowerCase();
    if (isReceive) return { colorClass: 'green', icon: <ArrowUpRight size={18} strokeWidth={2.5} /> };
    if (c.includes('tea') || c.includes('soda') || c.includes('snack')) return { colorClass: 'orange', icon: <Coffee size={18} /> };
    if (c.includes('food') || c.includes('meal')) return { colorClass: 'orange', icon: <Utensils size={18} /> };
    if (c.includes('gpay') || c.includes('digital')) return { colorClass: 'blue', icon: <Smartphone size={18} /> };
    if (c.includes('cash')) return { colorClass: 'teal', icon: <Banknote size={18} /> };
    if (c.includes('rent') || c.includes('bill')) return { colorClass: 'indigo', icon: <Home size={18} /> };
    if (c.includes('travel') || c.includes('fuel')) return { colorClass: 'teal', icon: <Car size={18} /> };
    return { colorClass: 'red', icon: <ArrowDownLeft size={18} strokeWidth={2.5} /> };
  };

  const { colorClass, icon } = getCategoryTheme(transaction.category_name);

  // Format currency
  const formatAmount = (val: number) => {
    return val.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  return (
    <div
      className="ios-list-row"
      style={{
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'relative'
      }}
    >
      {/* Left Info: User Profile Photo / Category Squircle + Titles */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
        {/* Member Profile Avatar Squircle with Category Badge Overlay */}
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <div
            className={`ios-icon-squircle ${colorClass}`}
            style={{
              width: '42px',
              height: '42px',
              borderRadius: '13px',
              fontWeight: 800,
              fontSize: '0.95rem',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.18)'
            }}
          >
            {transaction.user_avatar_url ? (
              <img
                src={transaction.user_avatar_url}
                alt={transaction.user_full_name || transaction.username || 'Member'}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : transaction.username ? (
              <span style={{ color: '#ffffff', fontWeight: 800 }}>
                {(transaction.user_full_name || transaction.username).charAt(0).toUpperCase()}
              </span>
            ) : (
              icon
            )}
          </div>

          {/* Small Category Accent Badge on bottom-right corner */}
          <div
            style={{
              position: 'absolute',
              bottom: '-3px',
              right: '-3px',
              width: '18px',
              height: '18px',
              borderRadius: '50%',
              background: 'var(--surface-primary)',
              border: '1.5px solid var(--border-hairline)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.62rem',
              fontWeight: 800,
              color: isReceive ? 'var(--ios-green)' : 'var(--ios-red)',
              boxShadow: '0 2px 4px rgba(0,0,0,0.25)'
            }}
            title={transaction.category_name || 'Category'}
          >
            {isReceive ? '↓' : '↑'}
          </div>
        </div>

        {/* Description & Subtext */}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{
            fontWeight: 600,
            fontSize: '0.9375rem',
            color: 'var(--text-primary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            letterSpacing: '-0.015em'
          }}>
            {transaction.description}
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginTop: '3px',
            flexWrap: 'wrap'
          }}>
            {/* Timestamp Subtitle */}
            <span style={{
              fontSize: '0.75rem',
              color: 'var(--text-tertiary)',
              letterSpacing: '-0.01em',
              fontWeight: 500
            }}>
              {transaction.transaction_date.slice(0, 16)}
            </span>

            {/* Category Pill Tag */}
            {transaction.category_name && (
              <span style={{
                fontSize: '0.6875rem',
                padding: '1px 6px',
                borderRadius: '6px',
                background: 'rgba(118, 118, 128, 0.18)',
                color: 'var(--text-secondary)',
                fontWeight: 600
              }}>
                {transaction.category_name}
              </span>
            )}

            {/* Member Name Tag */}
            {transaction.username && (
              <span style={{
                fontSize: '0.72rem',
                color: 'var(--ios-blue)',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '3px'
              }}>
                <User size={11} />
                <span>{transaction.user_full_name || transaction.username}</span>
              </span>
            )}

            {/* Receipt Attachment Thumbnail Badge */}
            {transaction.receipt_url && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsLightboxOpen(true);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '3px',
                  padding: '1px 6px',
                  borderRadius: 'var(--radius-pill)',
                  background: 'rgba(10, 132, 255, 0.12)',
                  border: '0.5px solid rgba(10, 132, 255, 0.3)',
                  color: 'var(--ios-blue)',
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
                title="View attached receipt image"
              >
                <Paperclip size={10} />
                <span>Receipt</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Right Amount & Actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
        <div style={{ textAlign: 'right' }}>
          <div
            className="num-font"
            style={{
              fontSize: '1.0625rem',
              fontWeight: 700,
              color: isReceive ? 'var(--ios-green)' : 'var(--text-primary)',
              letterSpacing: '-0.02em'
            }}
          >
            {isReceive ? '+' : '-'}₹{formatAmount(transaction.amount)}
          </div>
          <div style={{
            fontSize: '0.6875rem',
            color: 'var(--text-tertiary)',
            textTransform: 'uppercase',
            letterSpacing: '0.03em',
            fontWeight: 600
          }}>
            {isAdmin 
              ? (isReceive ? 'Received by Admin' : 'Paid by Admin')
              : (isReceive ? 'Paid by You' : 'Received by You')}
          </div>
          {transaction.running_balance !== undefined && (
            <div
              className="num-font"
              style={{
                fontSize: '0.6875rem',
                color: transaction.running_balance > 0
                  ? 'var(--ios-green)'
                  : transaction.running_balance < 0
                    ? 'var(--ios-red)'
                    : 'var(--text-tertiary)',
                fontWeight: 600,
                marginTop: '1px'
              }}
              title="Balance after this transaction"
            >
              Bal: {transaction.running_balance >= 0 ? '+' : '-'}₹{formatAmount(Math.abs(transaction.running_balance))}
            </div>
          )}
        </div>

        {/* Admin Direct Action Buttons: Edit and Delete */}
        {isAdmin && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: '4px' }}>
            {onEdit && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(transaction);
                }}
                className="ios-tap-target"
                style={{
                  background: 'rgba(10, 132, 255, 0.14)',
                  border: '0.5px solid rgba(10, 132, 255, 0.3)',
                  color: 'var(--ios-blue)',
                  cursor: 'pointer',
                  width: '32px',
                  height: '32px',
                  borderRadius: '9px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all var(--duration-fast)',
                  padding: 0
                }}
                title={`Edit "${transaction.description}"`}
                aria-label="Edit transaction"
              >
                <Edit2 size={14} strokeWidth={2.4} />
              </button>
            )}

            {onDelete && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (window.confirm(`Delete transaction "${transaction.description}" (₹${transaction.amount})?\n\nThis will permanently delete this record and reverse the balance impact.`)) {
                    onDelete(transaction.id);
                  }
                }}
                className="ios-tap-target"
                style={{
                  background: 'rgba(255, 69, 58, 0.14)',
                  border: '0.5px solid rgba(255, 69, 58, 0.3)',
                  color: 'var(--ios-red)',
                  cursor: 'pointer',
                  width: '32px',
                  height: '32px',
                  borderRadius: '9px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all var(--duration-fast)',
                  padding: 0
                }}
                title={`Delete "${transaction.description}"`}
                aria-label="Delete transaction"
              >
                <Trash2 size={14} strokeWidth={2.4} />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Lightbox Receipt Viewer */}
      {transaction.receipt_url && (
        <LightboxModal
          isOpen={isLightboxOpen}
          onClose={() => setIsLightboxOpen(false)}
          imageUrl={transaction.receipt_url}
          title={`Receipt #${transaction.id} — ${transaction.description}`}
        />
      )}
    </div>
  );
};
